import { priceEvidence, USER_PRICE, type PriceEvidence } from '@repo/food-engine'
import type { PriceBasis, QtyStatus, SimulationPublic } from './schemas/simulation'
import type { SourceTag } from './source-type'

// 단가 근거의 값·판정은 공통 엔진(packages/food-engine/src/evidence.ts)에 있다. 저장된 결과의 칸이라 이름을 그대로 다시 내보낸다
export {
  MISSING_PRICE,
  PRICE_EVIDENCE_SOURCES,
  PRICE_EVIDENCE_STATUSES,
  priceEvidence,
  USER_PRICE,
  type PriceEvidence,
} from '@repo/food-engine'

export const COST_COVERAGE_COPY = {
  en: {
    missing: 'Price incomplete',
    cost: 'Estimated manufacturing cost',
    margin: 'Estimated manufacturing margin',
    /** 좁은 칸(결과 요약 줄·홈 제품 카드)용 짧은 라벨. 원가 기준이라는 설명은 scope·자세히에 있다 */
    marginShort: 'Est. margin',
    scope:
      'Ingredients, packaging and labor only. Shipping, selling fees, taxes and fixed costs are excluded. This is not net profit; finished yield is not verified.',
    provided: 'User-entered price · not independently verified',
    estimated: 'Estimated price',
    unknown: 'Price not provided',
  },
  ko: {
    missing: '단가 일부 미확인',
    cost: '추정 제조원가',
    margin: '제조원가 기준 추정 마진',
    marginShort: '추정 마진',
    scope:
      '재료·포장·노무만 포함합니다. 배송·판매수수료·세금·고정비는 제외되며 순이익이 아닙니다. 완제품 수율은 검증되지 않았습니다.',
    provided: '사용자 입력 단가 · 별도 검증 전',
    estimated: '추정 단가',
    unknown: '단가 미입력',
  },
  ja: {
    missing: '一部単価が未確認',
    cost: '推定製造原価',
    margin: '製造原価に基づく推定利益率',
    marginShort: '推定利益率',
    scope:
      '材料・包装・人件費のみ。配送・販売手数料・税金・固定費は含まず、純利益ではありません。製品歩留まりは未検証です。',
    provided: 'ユーザー入力単価・独立検証前',
    estimated: '推定単価',
    unknown: '単価未入力',
  },
  zh: {
    missing: '部分单价未确认',
    cost: '估算制造成本',
    margin: '基于制造成本的估算利润率',
    marginShort: '估算利润率',
    scope:
      '仅含食材、包装和人工。不含配送、销售手续费、税金和固定成本，不代表净利润。成品收率尚未验证。',
    provided: '用户输入单价 · 未独立验证',
    estimated: '估算单价',
    unknown: '未填写单价',
  },
} as const

/**
 * 재료 한 줄의 단가 근거에 분량 확인 상태를 잇는다 (C40). 대략 분량(needs_check)의 qty 는 원문을 옮긴 추정치라
 * 사용자가 단가를 넣었어도 그 줄 원가는 추정이다 — 출처(user_input)는 남기고 estimated 로 낮춘다.
 * 원가가 0 이면 분량과 무관하게 0 이라 그대로 둔다. 저장 때 서버가 정하므로 옛 앱·공유 화면도 기존 근거 표시로 '추정'을 본다.
 */
export function qtyCheckedEvidence(
  evidence: PriceEvidence,
  cost: number,
  qtyStatus: QtyStatus | undefined,
): PriceEvidence {
  if (evidence.source !== 'user_input') return evidence
  return qtyStatus === 'needs_check' && cost > 0
    ? { source: 'user_input', status: 'estimated' }
    : { ...USER_PRICE }
}

export function estimatedPrice(
  value: number,
  source: 'model_estimate' | 'search_estimate',
): PriceEvidence {
  return priceEvidence(value, value > 0 ? { source, status: 'estimated' } : undefined)
}

export interface CostCoverage {
  complete: boolean
  missingIngredients: number
  packagingKnown: boolean
  laborKnown: boolean
  scope: 'ingredients_packaging_labor'
}

/** Use the currently selected price; an unused original/override cannot cover a missing substitute. */
export function assessCostCoverage(
  result: Pick<SimulationPublic, 'ingredients' | 'costs'>,
  selection: Readonly<Record<string, number | null>> = {},
  overrides: Readonly<Record<string, number>> = {},
): CostCoverage {
  const missingIngredients = result.ingredients.filter((ingredient) => {
    const pick = selection[ingredient.id]
    const selected = pick === undefined || pick === null ? ingredient : ingredient.substitutes[pick]
    if (!selected) return true
    const original = pick === undefined || pick === null
    const entered = original && Object.prototype.hasOwnProperty.call(overrides, ingredient.id)
    return (
      priceEvidence(
        entered ? overrides[ingredient.id]! : selected.unitPrice,
        entered ? USER_PRICE : selected.priceEvidence,
      ).status === 'missing'
    )
  }).length
  const packagingKnown =
    priceEvidence(result.costs.packagingPerUnit, result.costs.evidence?.packaging).status !==
    'missing'
  const laborKnown =
    priceEvidence(result.costs.laborPerUnit, result.costs.evidence?.labor).status !== 'missing'
  return {
    complete:
      result.ingredients.length > 0 && missingIngredients === 0 && packagingKnown && laborKnown,
    missingIngredients,
    packagingKnown,
    laborKnown,
    scope: 'ingredients_packaging_labor',
  }
}

/** Model-supplied provenance is ignored; only an unchanged base field can retain its evidence. */
export function reclassifyAiCostEvidence(
  candidate: SimulationPublic,
  base: SimulationPublic,
): SimulationPublic {
  const sameCurrency = candidate.currency === base.currency
  return {
    ...candidate,
    ingredients: candidate.ingredients.map((ingredient) => {
      const previous = base.ingredients.find((item) => item.id === ingredient.id)
      const unchanged =
        sameCurrency &&
        previous &&
        ['name', 'unit', 'unitPrice'].every(
          (key) =>
            previous[key as 'name' | 'unit' | 'unitPrice'] ===
            ingredient[key as 'name' | 'unit' | 'unitPrice'],
        )
      return {
        ...ingredient,
        priceEvidence: unchanged
          ? priceEvidence(ingredient.unitPrice, previous.priceEvidence)
          : estimatedPrice(ingredient.unitPrice, 'model_estimate'),
        substitutes: ingredient.substitutes.map((substitute) => {
          const old =
            sameCurrency &&
            previous?.substitutes.find(
              (item) =>
                item.name === substitute.name &&
                item.unit === substitute.unit &&
                item.unitPrice === substitute.unitPrice,
            )
          return {
            ...substitute,
            priceEvidence: old
              ? priceEvidence(substitute.unitPrice, old.priceEvidence)
              : estimatedPrice(substitute.unitPrice, 'model_estimate'),
          }
        }),
      }
    }),
    costs: {
      ...candidate.costs,
      coverage: undefined,
      evidence: {
        packaging:
          sameCurrency && candidate.costs.packagingPerUnit === base.costs.packagingPerUnit
            ? priceEvidence(base.costs.packagingPerUnit, base.costs.evidence?.packaging)
            : estimatedPrice(candidate.costs.packagingPerUnit, 'model_estimate'),
        labor:
          sameCurrency && candidate.costs.laborPerUnit === base.costs.laborPerUnit
            ? priceEvidence(base.costs.laborPerUnit, base.costs.evidence?.labor)
            : estimatedPrice(candidate.costs.laborPerUnit, 'model_estimate'),
      },
    },
  }
}

/**
 * 사용자 입력 단가 근거(#6)의 유효 상태. today 는 YYYY-MM-DD (서버·앱이 오늘 날짜를 넘긴다 — 같은 입력이면 같은 답).
 * - expired: 유효기간이 지났다 → 화면에 '확인 필요'
 * - current: 유효기간 안이다 · undated: 유효기간을 적지 않았다 (기한을 모르니 지났다고도 하지 않는다)
 */
export function priceBasisStatus(
  basis: PriceBasis | undefined,
  today: string,
): 'current' | 'expired' | 'undated' {
  if (!basis?.validUntil) return 'undated'
  return basis.validUntil < today ? 'expired' : 'current'
}

/**
 * 단가 하나의 근거 꼬리표 (source-type.ts). 단가 근거(PriceEvidence)를 명세 P1-03 의 꼬리표로 옮긴다.
 * 사용자 입력 단가에 공급사가 적혀 있으면 공급사 견적, 추정 단가는 AI 추정(검색 시세도 모델이 고른 값이라 확인 전),
 * 없는 단가는 미확인. 분량 확인 전(qtyCheckedEvidence 가 estimated 로 낮춘 사용자 단가)도 사용자 입력으로 둔다 —
 * 단가 자체는 사람이 넣은 값이고, 분량 확인은 재료 줄의 '확인 필요'로 따로 보인다
 */
export function priceSourceTag(
  value: number,
  evidence: PriceEvidence | undefined,
  basis?: PriceBasis,
): SourceTag {
  const resolved = priceEvidence(value, evidence)
  if (resolved.status === 'missing') return 'unconfirmed'
  if (resolved.source !== 'user_input') return 'ai_estimated'
  return basis?.supplier ? 'supplier_quote' : 'user_input'
}

/**
 * 저장할 때 칸을 모르는 옛 앱이 보낸 결과에도 저장돼 있던 단가 근거(#6)를 지킨다 (keepSavedServingSize 와 같은 이유).
 * 같은 재료(id)의 이름·단위·단가가 그대로일 때만 옮긴다 — 단가가 바뀌면 예전 근거는 맞지 않는다
 */
export function keepSavedPriceBasis<T extends Pick<SimulationPublic, 'ingredients'>>(
  next: T,
  saved: Pick<SimulationPublic, 'ingredients'> | null | undefined,
): T {
  if (!saved) return next
  let changed = false
  const ingredients = next.ingredients.map((ingredient) => {
    if (ingredient.priceBasis) return ingredient
    const old = saved.ingredients.find((item) => item.id === ingredient.id)
    if (
      !old?.priceBasis ||
      old.name !== ingredient.name ||
      old.unit !== ingredient.unit ||
      old.unitPrice !== ingredient.unitPrice
    )
      return ingredient
    changed = true
    return { ...ingredient, priceBasis: old.priceBasis }
  })
  return changed ? { ...next, ingredients } : next
}
